import React from 'react';
import { useParams } from 'react-router-dom';
import Spinner from '../../components/spinner/spinner';
import orderGateHook from '../../api/orderGateHook';
import useDeviceInfo from '../../helpers/deviceDetect';

// The invoice a student opens is the one the business issued - the same PDF, not
// a second drawing of it.
//
// This page used to render its own, in the browser, from a template of its own
// (pdfRender.jsx). Two templates for one statutory document is one too many: it
// drifted from the server's layout, and it could not have caught up even in
// principle, because the invoice API hands this page about a third of what the
// document prints. No roll number, no purchase reference, no batch, no venue, no
// payment-mode columns, no QR, no promotional-event rows - all of them resolved
// in payment-service at render time and none of them on the wire. The browser
// copy also disagreed on substance, not only on looks: it elected a combo's
// course as comboElements[0] rather than the primary element, and a precedence
// slip in its GSTIN expression meant every invoice it drew carried a hardcoded
// GSTIN instead of the branch's.
//
// So it shows the document itself. normalizeInvoice signs a URL for the PDF the
// server rendered and uploaded, inline and named by its invoice number, and
// order-details already opens invoices this way - this page was the last one
// drawing its own.
function InvoicePage() {
  const { invoiceId, orgId } = useParams();
  const { device, isWebView } = useDeviceInfo();

  const [data, loading, error] = orderGateHook.useGetInvoiceDetails({ invoiceId, orgId });

  const pdfURL = data?.pdfURL;

  // The Android app cannot follow a signed URL out of its WebView, so it is
  // handed over the bridge instead. downloadInvoiceUrlNew, not downloadInvoiceUrl:
  // the older bridge method takes base64 bytes, which is what this page used to
  // have to produce. order-details calls the same method with the same kind of
  // URL.
  const openInvoice = () => {
    if (isWebView && device === 'android') {
      // eslint-disable-next-line no-undef
      Android.downloadInvoiceUrlNew(pdfURL);
      return;
    }

    const link = document.createElement('a');
    link.href = pdfURL;
    link.target = '_blank';
    link.download = pdfURL;
    link.click();
  };

  if (loading) return <Spinner fullPage />;
  if (error) return null;

  // An invoice is raised the moment the payment settles; its PDF is rendered and
  // uploaded afterwards, by the pending-invoice cron. Between the two there is
  // genuinely nothing to show, so this says so in the words order-details
  // already uses rather than leaving a student at a blank frame.
  if (!pdfURL) {
    return (
      <div className="flex items-center justify-center h-screen px-6 text-center">
        No invoices available yet. Please check back in an hour or contact support.
      </div>
    );
  }

  return (
    <>
      {/* Read on a screen with room for a page of A4; the toolbar is the
          browser's own, so printing and saving come free. */}
      <div className="w-full hidden md:block">
        <iframe
          src={pdfURL}
          title="Invoice"
          style={{ width: '100%', height: '100vh', border: 'none' }}
        />
      </div>

      {/* On a phone an embedded A4 page is unreadable, so the document is
          handed to whatever the device opens PDFs with. */}
      <div className="w-screen h-screen md:hidden flex items-center justify-center">
        <button
          type="button"
          onClick={openInvoice}
          className="h-fit px-6 py-3 border border-blue-600 text-blue-600 font-semibold rounded-lg shadow-md transition duration-300"
        >
          Download Invoice
        </button>
      </div>
    </>
  );
}

export default InvoicePage;
